import type { Arealplan } from '@origo/planregister';
import type { FilterOption, PlanFilters, SortOption } from './types';

/** Arealplan-felt som har et kodeliste-filter. */
export type FilterField = 'planstatus' | 'plantype' | 'lovreferanse' | 'admomrkode';

/** Unike verdier for et felt, talt opp og sortert (rå koder; visningstekst legges på i UI). */
export function getFilterOptions(plans: readonly Arealplan[], field: FilterField): FilterOption[] {
    const counts = new Map<string, number>();
    for (const plan of plans) {
        const val = plan[field];
        if (val != null && val !== '') {
            const key = String(val);
            counts.set(key, (counts.get(key) ?? 0) + 1);
        }
    }
    return [...counts.entries()]
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => a.value.localeCompare(b.value, 'nb'));
}

export function hasActiveFilters(f: PlanFilters): boolean {
    return (
        f.statuses.length > 0 ||
        f.plantyper.length > 0 ||
        f.lovreferanser.length > 0 ||
        f.kommuner.length > 0 ||
        f.dateFrom !== null ||
        f.dateTo !== null
    );
}

export function countActiveFilters(f: PlanFilters): number {
    let count = f.statuses.length + f.plantyper.length + f.lovreferanser.length + f.kommuner.length;
    if (f.dateFrom) count++;
    if (f.dateTo) count++;
    return count;
}

/** Dato filtreres mot vedtaksdato, med fallback til ikrafttredelsesdato. */
function matchesDateRange(plan: Arealplan, dateFrom: Date | null, dateTo: Date | null): boolean {
    if (!dateFrom && !dateTo) return true;
    const date = plan.vedtaksdato ?? plan.ikrafttredelsesdato;
    if (!date) return false;
    const t = date.getTime();
    if (dateFrom && t < dateFrom.getTime()) return false;
    if (dateTo) {
        const endOfDay = new Date(dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        if (t > endOfDay.getTime()) return false;
    }
    return true;
}

export function applyFilters(plans: readonly Arealplan[], f: PlanFilters): Arealplan[] {
    if (!hasActiveFilters(f)) return [...plans];
    return plans.filter(
        plan =>
            (f.statuses.length === 0 || f.statuses.includes(plan.planstatus)) &&
            (f.plantyper.length === 0 || f.plantyper.includes(plan.plantype)) &&
            (f.lovreferanser.length === 0 || f.lovreferanser.includes(plan.lovreferanse)) &&
            (f.kommuner.length === 0 || f.kommuner.includes(plan.admomrkode)) &&
            matchesDateRange(plan, f.dateFrom, f.dateTo)
    );
}

export function sortPlans(plans: readonly Arealplan[], sort: SortOption): Arealplan[] {
    const sorted = [...plans];
    const dir = sort.direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
        if (sort.field === 'vedtaksdato' || sort.field === 'ikrafttredelsesdato') {
            const at = a[sort.field]?.getTime() ?? 0;
            const bt = b[sort.field]?.getTime() ?? 0;
            return (at - bt) * dir;
        }
        return String(a[sort.field] ?? '').localeCompare(String(b[sort.field] ?? ''), 'nb') * dir;
    });
    return sorted;
}

/** Dato `months` måneder tilbake i tid (00:00:00). */
export function monthsAgo(months: number): Date {
    const d = new Date();
    d.setMonth(d.getMonth() - months);
    d.setHours(0, 0, 0, 0);
    return d;
}
